import CategoryDescription from 'components/CategoryDescription';
import { DATA_SOURCES } from '@/config/constants';
import EntityBreadcrumbs from '../shared/entity/EntityBreadcrumbs';
import EntityHeroCard from '../shared/entity/EntityHeroCard';
import EntityPage from '../shared/entity/EntityPage';
import EntityRelatedTable from '../shared/entity/EntityRelatedTable';
import EntityTabs, { EntityTab } from '../shared/entity/EntityTabs';
import EntityTopPosts from '../shared/entity/EntityTopPosts';
import EntityTopTerms from '../shared/entity/EntityTopTerms';
import { getInstagramHealthStats } from 'state/utils';
import { INSTAGRAM_METADATA_BY_KEY } from 'utils/metrics';
import InstagramBreadCrumbs from 'components/Breadcrumbs/InstagramBreadCrumbs';
import { InstagramChartCard } from '../shared/entity/chartCards';
import {
    InstagramRelatedBrandsSectionTable,
    InstagramRelatedCategoriesSectionTable,
} from 'components/DataVis/InstagramRelatedCategoriesTable';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopInstagramHashtagsSection } from 'components/TopKeywordsSection';
import TopPostsSection from 'components/Posts/Instagram/TopPostsSection/CategoryTopPostsSection';
import useInstagramEntity from '../shared/entity/useInstagramEntity';
import { useTrackCategoryView } from 'utils/hooks/useTrackEntityView';

const METRICS = [INSTAGRAM_METADATA_BY_KEY.likes];

function InstagramCategoryFivePage({ categoryId, controller, tabs }) {
    const page = useInstagramEntity({
        entityType: 'categories',
        entityId: categoryId,
        controller,
        coverage: 'hint',
    });
    const { details, entityQuery, name, row } = page;

    const healthStats = getInstagramHealthStats(row);

    useTrackCategoryView(DATA_SOURCES.INSTAGRAM, {
        id: categoryId,
        categoryName: name,
        countryName: page.country?.name || null,
    });

    // `isEmpty` is stricter than the hook's: a row can arrive without likes.
    return (
        <EntityPage
            page={page}
            breadcrumbs={<EntityBreadcrumbs as={InstagramBreadCrumbs} />}
            sourceTabs={tabs}
            title={{ name, isLoading: entityQuery.isLoading }}
            actions={
                <ToggleWatch
                    id={categoryId}
                    data={{ name, type: 'category' }}
                />
            }
            sections={
                <>
                    <EntityTopTerms as={TopInstagramHashtagsSection} />
                    <EntityTopPosts as={TopPostsSection} />
                </>
            }
            tabs={
                <EntityTabs>
                    <EntityTab name="Related Categories">
                        <EntityRelatedTable
                            as={InstagramRelatedCategoriesSectionTable}
                            subject="categories"
                            icon="affinity"
                            title="Related Categories"
                            exportType="related-category"
                            exportEntity={name || 'trend'}
                            sortParamKey="relatedSort"
                        />
                    </EntityTab>
                    <EntityTab name="Related Brands">
                        <EntityRelatedTable
                            as={InstagramRelatedBrandsSectionTable}
                            subject="brands"
                            icon="brand"
                            title="Related Brands"
                            exportType="related-brand"
                            exportEntity={name || 'trend'}
                            sortParamKey="brandSort"
                        />
                    </EntityTab>
                </EntityTabs>
            }
        >
            <div className="flex flex-col gap-3">
                <EntityHeroCard healthStats={healthStats} />
                <CategoryDescription
                    categoryId={categoryId}
                    categoryName={name}
                    flavors={details?.flavors}
                />
                <InstagramChartCard
                    metricList={METRICS}
                    healthStats={healthStats}
                    chart={{ cluster: row?.cluster }}
                />
            </div>
        </EntityPage>
    );
}

InstagramCategoryFivePage.displayName = 'InstagramCategoryFive';

export default InstagramCategoryFivePage;
